import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { departmentAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';

export default function DepartmentsManager() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    departmentAPI.getAll()
      .then(res => setDepartments(res.data))
      .catch(() => toast.error('Failed to load departments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await departmentAPI.create(newName.trim());
      toast.success('Department added');
      setNewName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add department');
    }
    setBusy(false);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    setBusy(true);
    try {
      await departmentAPI.update(editingId, editName.trim());
      toast.success('Department updated');
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update department');
    }
    setBusy(false);
  };

  const handleDelete = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}"?`)) return;
    setBusy(true);
    try {
      await departmentAPI.delete(dept.id);
      toast.success('Department deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete department');
    }
    setBusy(false);
  };

  return (
    <Card data-testid="departments-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Departments
        </CardTitle>
        <CardDescription>
          Manage the departments available when creating or editing employees
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" />Loading…
          </div>
        ) : (
          <div className="space-y-1.5">
            {departments.map(function(dept) {
              const isEditing = editingId === dept.id;
              return (
                <div key={dept.id} className="flex items-center justify-between rounded-md border px-3 py-1.5" data-testid={'dept-row-' + dept.id}>
                  {isEditing ? (
                    <Input
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleSaveEdit())}
                      className="h-8 max-w-[260px]"
                      autoFocus
                      data-testid="dept-edit-input"
                    />
                  ) : (
                    <span className="text-sm">{dept.name}</span>
                  )}
                  <div className="flex items-center gap-1">
                    {isEditing ? (
                      <>
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" onClick={handleSaveEdit} disabled={busy} data-testid="dept-save-btn">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingId(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingId(dept.id); setEditName(dept.name); }} data-testid={'dept-edit-' + dept.id}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(dept)} disabled={busy} data-testid={'dept-delete-' + dept.id}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {departments.length === 0 && <p className="text-sm text-muted-foreground">No departments yet — add one below.</p>}
          </div>
        )}

        <div className="flex gap-2">
          <Input
            placeholder="Add new department…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
            className="max-w-[260px]"
            data-testid="new-dept-input"
          />
          <Button type="button" variant="outline" onClick={handleAdd} disabled={!newName.trim() || busy} data-testid="add-dept-btn">
            <Plus className="h-4 w-4 mr-2" />Add
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Departments with employees assigned cannot be deleted — reassign those employees first.
        </p>
      </CardContent>
    </Card>
  );
}
